import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import type { TFetchStatus } from "./types";

export type TDocumentTypeCriteria = {
    /** `beneficiar.precount` (the country's precount options) */
    precount: number | null;
    /** `beneficiar.vat` values (VAT profiles of the company country) */
    vatProfiles: number[] | null;
    entity: "legal" | "physical" | null;
    /** ISO country codes of residence */
    countries: string[] | null;
    /** `true`: residing OUTSIDE `countries` (e.g. abroad = outside the company country) */
    excludeCountries: boolean | null;
    /** URSSAF social profile codes */
    socialProfileTypes: string[] | null;
};

export const emptyCriteria: TDocumentTypeCriteria = {
    precount: null,
    vatProfiles: null,
    entity: null,
    countries: null,
    excludeCountries: null,
    socialProfileTypes: null,
};

export interface IBeneficiaryDocumentType {
    id: number;
    companyId: number;
    name: string;
    requiresValidity: boolean;
    criteria: TDocumentTypeCriteria;
    isActive: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string | null;
}

export type TDocumentTypePayload = {
    name: string;
    requiresValidity: boolean;
    criteria: TDocumentTypeCriteria;
    isActive?: boolean;
};

const endpoint = () => `${import.meta.env.REACT_APP_API_ENDPOINT}/company/document-types`;

export type TDocumentTypeBeneficiariesCount = { matching: number; total: number };
export type TDocumentTypeCriteriaOptions = { countries: string[] };

/** Values the criteria form can offer: the residence countries actually set on active beneficiaries. */
export const fetchDocumentTypeCriteriaOptions = async () => {
    const response = await axios.get(`${endpoint()}/criteria-options`);
    return response.data.data as TDocumentTypeCriteriaOptions;
};

/** Active beneficiaries a type with these criteria would be expected from (form preview, not stored). */
export const countDocumentTypeBeneficiaries = async (criteria: TDocumentTypeCriteria) => {
    const response = await axios.post(`${endpoint()}/beneficiaries-count`, { criteria });
    return response.data.data as TDocumentTypeBeneficiariesCount;
};

export const fetchDocumentTypes = createAsyncThunk("beneficiaryDocumentTypes/fetch", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(endpoint());
        return response.data.data as IBeneficiaryDocumentType[];
    } catch (error: any) {
        return rejectWithValue(error.response?.data);
    }
});

export const createDocumentType = createAsyncThunk(
    "beneficiaryDocumentTypes/create",
    async (data: TDocumentTypePayload, { rejectWithValue }) => {
        try {
            const response = await axios.post(endpoint(), data);
            return response.data.data as IBeneficiaryDocumentType;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const updateDocumentType = createAsyncThunk(
    "beneficiaryDocumentTypes/update",
    async ({ id, data }: { id: number; data: Partial<TDocumentTypePayload> }, { rejectWithValue }) => {
        try {
            const response = await axios.patch(`${endpoint()}/${id}`, data);
            return response.data.data as IBeneficiaryDocumentType;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const deleteDocumentType = createAsyncThunk(
    "beneficiaryDocumentTypes/delete",
    async (id: number, { rejectWithValue }) => {
        try {
            await axios.delete(`${endpoint()}/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

interface State {
    documentTypes: IBeneficiaryDocumentType[];
    status: TFetchStatus;
    saveStatus: TFetchStatus;
}

const initialState: State = { documentTypes: [], status: "idle", saveStatus: "idle" };

const beneficiaryDocumentTypesSlice = createSlice({
    name: "beneficiaryDocumentTypes",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentTypes.pending, (state) => {
                state.status = "pending";
            })
            .addCase(fetchDocumentTypes.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.documentTypes = action.payload;
            })
            .addCase(fetchDocumentTypes.rejected, (state) => {
                state.status = "rejected";
            })
            .addCase(createDocumentType.fulfilled, (state, action) => {
                state.saveStatus = "fulfilled";
                state.documentTypes.push(action.payload);
            })
            .addCase(updateDocumentType.fulfilled, (state, action) => {
                state.saveStatus = "fulfilled";
                state.documentTypes = state.documentTypes.map((type) =>
                    type.id === action.payload.id ? action.payload : type,
                );
            })
            .addCase(deleteDocumentType.fulfilled, (state, action) => {
                state.saveStatus = "fulfilled";
                state.documentTypes = state.documentTypes.filter((type) => type.id !== action.payload);
            });
        [createDocumentType, updateDocumentType, deleteDocumentType].forEach((thunk) => {
            builder.addCase(thunk.pending, (state) => {
                state.saveStatus = "pending";
            });
            builder.addCase(thunk.rejected, (state) => {
                state.saveStatus = "rejected";
            });
        });
    },
});

export default beneficiaryDocumentTypesSlice.reducer;
